import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import ReportCard from '../../components/report-builder/ReportCard';
import type { SavedReportConfig } from '../../types/reportBuilder';
import { Plus, MagnifyingGlass, SquaresFour, List, CircleNotch, PencilSimple, Trash } from '@phosphor-icons/react';

type ViewMode = 'grid' | 'list';
type SortField = 'name' | 'created_at' | 'updated_at';

export default function ReportBuilderLibraryPage() {
  const navigate = useNavigate();
  const { savedReportConfigs, savedReportsLoading, fetchSavedReportConfigs, deleteReportFromAPI } = useReportBuilderStore();

  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortField, setSortField] = useState<SortField>('updated_at');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    fetchSavedReportConfigs();
  }, [fetchSavedReportConfigs]);

  const filtered = useMemo(() => {
    // Filter out reports restricted by subscription
    let items = savedReportConfigs.filter(r => !r.restricted);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(r => r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
    }
    return [...items].sort((a, b) => {
      if (sortField === 'name') return a.name.localeCompare(b.name);
      return new Date(b[sortField]).getTime() - new Date(a[sortField]).getTime();
    });
  }, [savedReportConfigs, search, sortField]);

  const handleView = (id: string) => navigate(`/analytics/report-builder/${id}`);
  const handleEdit = (id: string) => navigate(`/analytics/report-builder/${id}/edit`);
  const handleDelete = async (id: string) => {
    await deleteReportFromAPI(id);
    setDeleteConfirmId(null);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Custom Reports</h1>
            <p className="text-sm text-gray-500">Create and manage custom reports</p>
          </div>
          <button
            onClick={() => navigate('/analytics/report-builder/new')}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90"
          >
            <Plus size={16} weight="bold" /> Create New Report
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search reports..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Sort */}
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value as SortField)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="updated_at">Last Updated</option>
            <option value="created_at">Date Created</option>
            <option value="name">Name</option>
          </select>

          {/* View Toggle */}
          <div className="inline-flex rounded-lg border border-gray-300 bg-white">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1 rounded-l-lg px-3 py-2 text-sm ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <SquaresFour size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1 rounded-r-lg px-3 py-2 text-sm ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {savedReportsLoading ? (
          <div className="flex h-64 items-center justify-center">
            <CircleNotch size={32} className="animate-spin text-blue-500" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState hasSearch={search.length > 0} onCreateNew={() => navigate('/analytics/report-builder/new')} />
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((report) => (
              <ReportCard
                key={report.id}
                report={report}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={(id) => setDeleteConfirmId(id)}
              />
            ))}
          </div>
        ) : (
          <ReportListView
            reports={filtered}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={(id) => setDeleteConfirmId(id)}
          />
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <DeleteConfirmDialog
          onConfirm={() => handleDelete(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      )}
    </div>
  );
}

function EmptyState({ hasSearch, onCreateNew }: { hasSearch: boolean; onCreateNew: () => void }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200">
      {hasSearch ? (
        <>
          <p className="text-sm text-gray-500">No reports match your search</p>
          <p className="mt-1 text-xs text-gray-400">Try a different search term</p>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-500">No saved reports yet</p>
          <p className="mt-1 text-xs text-gray-400">Create your first report to get started</p>
          <button
            onClick={onCreateNew}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Plus size={16} weight="bold" /> Create Report
          </button>
        </>
      )}
    </div>
  );
}

function ReportListView({
  reports,
  onView,
  onEdit,
  onDelete,
}: {
  reports: SavedReportConfig[];
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Name</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Models</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Created</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Updated</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {reports.map((r) => (
            <tr key={r.id} onClick={() => onView(r.id)} className="cursor-pointer hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-gray-900">{r.name}</p>
                {r.description && <p className="text-xs text-gray-500 line-clamp-1">{r.description}</p>}
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {r.model_keys.map((key) => (
                    <span key={key} className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">{key}</span>
                  ))}
                </div>
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
              <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">{new Date(r.updated_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={(e) => { e.stopPropagation(); onEdit(r.id); }} className="rounded p-1 text-gray-400 hover:bg-blue-50 hover:text-blue-600" title="Edit">
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onDelete(r.id); }} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500" title="Delete">
                    <Trash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeleteConfirmDialog({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-base font-semibold text-gray-900">Delete Report</h3>
        <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete this report? This action cannot be undone.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">Delete</button>
        </div>
      </div>
    </div>
  );
}
